// src/plugins/builder/zip.ts
// Minimal "stored" (no-compression) ZIP writer. No new npm deps.
//
// Why write our own? The plugin system ships with zero new dependencies — we
// keep that promise. A stored ZIP is a few hundred bytes of header code + a
// CRC-32 implementation, and the file still opens in Finder, Explorer,
// unzip, and Node. Compression would just be a nice-to-have here; the
// generated plugin folder is always a few small text files.
/** Produce a Blob (or Uint8Array) containing a valid stored ZIP. */
export function buildZip(entries) {
    const localParts = [];
    const centralParts = [];
    let offset = 0;
    const enc = new TextEncoder();
    for (const entry of entries) {
        const nameBytes = enc.encode(entry.path);
        const dataBytes = typeof entry.data === 'string' ? enc.encode(entry.data) : entry.data;
        const crc = crc32(dataBytes);
        const size = dataBytes.length;
        // ---- local file header --------------------------------------------------
        const lh = new Uint8Array(30 + nameBytes.length);
        const lhDv = new DataView(lh.buffer);
        lhDv.setUint32(0, 0x04034b50, true); // local file header signature
        lhDv.setUint16(4, 20, true); // version needed
        lhDv.setUint16(6, 0, true); // flags
        lhDv.setUint16(8, 0, true); // method: stored
        lhDv.setUint16(10, 0, true); // mod time
        lhDv.setUint16(12, 0x21, true); // mod date (1980-01-01 → 0x21)
        lhDv.setUint32(14, crc, true);
        lhDv.setUint32(18, size, true);
        lhDv.setUint32(22, size, true);
        lhDv.setUint16(26, nameBytes.length, true);
        lhDv.setUint16(28, 0, true);
        lh.set(nameBytes, 30);
        localParts.push(lh, dataBytes);
        // ---- central directory entry -------------------------------------------
        const cd = new Uint8Array(46 + nameBytes.length);
        const cdDv = new DataView(cd.buffer);
        cdDv.setUint32(0, 0x02014b50, true);
        cdDv.setUint16(4, 20, true); // version made by
        cdDv.setUint16(6, 20, true); // version needed
        cdDv.setUint16(8, 0, true); // flags
        cdDv.setUint16(10, 0, true); // method
        cdDv.setUint16(12, 0, true); // mod time
        cdDv.setUint16(14, 0x21, true); // mod date
        cdDv.setUint32(16, crc, true);
        cdDv.setUint32(20, size, true);
        cdDv.setUint32(24, size, true);
        cdDv.setUint16(28, nameBytes.length, true);
        cdDv.setUint16(30, 0, true); // extra len
        cdDv.setUint16(32, 0, true); // comment len
        cdDv.setUint16(34, 0, true); // disk #
        cdDv.setUint16(36, 0, true); // internal attrs
        cdDv.setUint32(38, 0, true); // external attrs
        cdDv.setUint32(42, offset, true); // relative offset of local header
        cd.set(nameBytes, 46);
        centralParts.push(cd);
        offset += lh.length + dataBytes.length;
    }
    const localBlob = concat(localParts);
    const centralBlob = concat(centralParts);
    // ---- end-of-central-directory -------------------------------------------
    const eocd = new Uint8Array(22);
    const eocdDv = new DataView(eocd.buffer);
    eocdDv.setUint32(0, 0x06054b50, true);
    eocdDv.setUint16(4, 0, true); // disk #
    eocdDv.setUint16(6, 0, true); // disk w/ CD
    eocdDv.setUint16(8, entries.length, true); // total on this disk
    eocdDv.setUint16(10, entries.length, true);
    eocdDv.setUint32(12, centralBlob.length, true);
    eocdDv.setUint32(16, localBlob.length, true);
    eocdDv.setUint16(20, 0, true);
    return concat([localBlob, centralBlob, eocd]);
}
// ---- helpers --------------------------------------------------------------
function concat(parts) {
    let total = 0;
    for (const p of parts)
        total += p.length;
    const out = new Uint8Array(total);
    let pos = 0;
    for (const p of parts) {
        out.set(p, pos);
        pos += p.length;
    }
    return out;
}
// CRC-32 (IEEE 802.3). Computed once per call — small plugin sources only.
let crcTable = null;
function crc32(buf) {
    if (!crcTable) {
        crcTable = new Uint32Array(256);
        for (let i = 0; i < 256; i++) {
            let c = i;
            for (let k = 0; k < 8; k++)
                c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
            crcTable[i] = c >>> 0;
        }
    }
    let c = 0xffffffff;
    for (let i = 0; i < buf.length; i++) {
        c = crcTable[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
    }
    return (c ^ 0xffffffff) >>> 0;
}
/** Browser-side download helper. No-op in test/Node environments. */
export function downloadZip(entries, filename) {
    if (typeof document === 'undefined' || typeof URL === 'undefined')
        return;
    const bytes = buildZip(entries);
    const blob = new Blob([bytes], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
